import { Vector3 } from '../../types/Vector3'
import { Quaternion } from '../../types/Quaternion'
import { LayoutInterface, AlignmentMode } from '../FieldTypes'
import { FieldParameters } from '../FieldParameters'
import { CurveUtils } from '../../../math/CurveUtils'
import { VectorUtils } from '../../../math/VectorUtils'

export class CircleLayout implements LayoutInterface {
  // ==================== POSITION CALCULATION ====================
  
  /**
   * Calculate position for object at given index
   */
  public computePosition(index: number, totalCount: number, params: FieldParameters): Vector3 {
    if (totalCount <= 1) {
      return new Vector3()
    }
    
    // Calculate angle for this index
    const angle = this.calculateAngle(index, totalCount)
    
    // Calculate radius
    const radius = this.calculateRadius(params, totalCount)
    
    // Get point on circle
    const position = CurveUtils.getPointOnCircle(
      new Vector3(), // Center at origin
      radius,
      angle,
      params.customUpVector
    )
    
    return position
  }
  
  // ==================== ORIENTATION CALCULATION ====================
  
  /**
   * Calculate orientation for object at given index using improved Frenet frame math
   */
  public computeOrientation(index: number, totalCount: number, params: FieldParameters): Quaternion {
    const angle = this.calculateAngle(index, totalCount)
    const radius = this.calculateRadius(params, totalCount)
    const center = new Vector3()
    
    // Get Frenet frame for the circle
    const frenetFrame = CurveUtils.getCircleFrenetFrame(
      center, 
      radius, 
      angle, 
      params.fieldDirection
    )
    
    // Determine up mode based on parameters
    const upMode = params.upVectorMode === 'world-up' ? 'world' : 'path'
    
    // Determine alignment mode
    const orientation = params.alignmentMode === AlignmentMode.Aligned ? 'aligned' : 'perpendicular'
    
    // Calculate orientation using the improved math
    const { right, up, forward } = CurveUtils.calculateOrientation(
      frenetFrame.tangent,
      frenetFrame.normal,
      frenetFrame.binormal,
      orientation,
      upMode
    )
    
    // Debug logging for first object only
    if (index === 0 && Math.floor(Date.now() / 5000) % 10 === 0) { // Log occasionally
      //console.log(`🔄 Circle ${orientation.toUpperCase()} ${upMode.toUpperCase()}-UP:`)
      //console.log(`  Angle: ${(angle * 180 / Math.PI).toFixed(1)}°`)
      //console.log(`  Tangent: [${frenetFrame.tangent.x.toFixed(2)}, ${frenetFrame.tangent.y.toFixed(2)}, ${frenetFrame.tangent.z.toFixed(2)}]`)
      //console.log(`  Normal:  [${frenetFrame.normal.x.toFixed(2)}, ${frenetFrame.normal.y.toFixed(2)}, ${frenetFrame.normal.z.toFixed(2)}]`)
      //console.log(`  Forward: [${forward.x.toFixed(2)}, ${forward.y.toFixed(2)}, ${forward.z.toFixed(2)}]`)
      //console.log(`  Up:      [${up.x.toFixed(2)}, ${up.y.toFixed(2)}, ${up.z.toFixed(2)}]`)
    }
    
    // Create rotation matrix from the basis vectors
    return VectorUtils.lookRotation(forward, up)
  }
  
  // ==================== TANGENT CALCULATION ====================
  
  /**
   * Calculate tangent vector (direction along circle)
   */
  public computeTangent(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const angle = this.calculateAngle(index, totalCount)
    return CurveUtils.getCircleTangent(angle, params.customUpVector)
  }
  
  // ==================== NORMAL CALCULATION ====================
  
  /**
   * Calculate normal vector (radial direction)
   */
  public computeNormal(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const position = this.computePosition(index, totalCount, params)
    const center = new Vector3()
    return VectorUtils.getInwardNormal(position, center)
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Calculate angle for given index
   */
  private calculateAngle(index: number, totalCount: number): number {
    if (totalCount <= 1) return 0
    
    // Distribute evenly around circle
    return (index / totalCount) * Math.PI * 2
  }
  
  /**
   * Get main directional vector of the layout (circle plane normal)
   */
  public getDirectionalVector(params: FieldParameters): Vector3 {
    return params.fieldDirection.clone().normalize()
  }
  
  /**
   * Calculate radius based on parameters
   */
  private calculateRadius(params: FieldParameters, totalCount: number): number {
    if (params.useObjectSpacing && params.objectBounds) {
      // Calculate radius based on object size and spacing
      const objectSize = params.objectBounds.size
      const maxSize = Math.max(objectSize.x, objectSize.y, objectSize.z)
      const circumference = totalCount * maxSize * params.spacingMultiplier
      return circumference / (Math.PI * 2)
    } else {
      // Use fixed radius
      return params.radius
    }
  }
  
  // ==================== ADVANCED FEATURES ====================
  
  /**
   * Get circle circumference
   */
  public getCircumference(totalCount: number, params: FieldParameters): number {
    const radius = this.calculateRadius(params, totalCount)
    return Math.PI * 2 * radius
  }
  
  /**
   * Get point at specific angle
   */
  public getPointAtAngle(angle: number, params: FieldParameters, totalCount: number): Vector3 {
    const radius = this.calculateRadius(params, totalCount)
    return CurveUtils.getPointOnCircle(
      new Vector3(),
      radius,
      angle,
      params.customUpVector
    )
  }
  
  /**
   * Find closest point on circle
   */
  public findClosestPoint(targetPoint: Vector3, totalCount: number, params: FieldParameters): {
    point: Vector3
    index: number
    distance: number
    angle: number
  } {
    const radius = this.calculateRadius(params, totalCount)
    const center = new Vector3()
    
    // Project point onto circle plane
    const toTarget = targetPoint.clone().subtract(center)
    const projected = toTarget.clone().projectOnPlane(params.customUpVector)
    
    // Calculate angle to projected point
    const angle = Math.atan2(projected.z, projected.x)
    const normalizedAngle = angle < 0 ? angle + Math.PI * 2 : angle
    
    // Find closest index
    const indexFloat = (normalizedAngle / (Math.PI * 2)) * totalCount
    const closestIndex = Math.round(indexFloat) % totalCount
    
    // Get actual point on circle
    const closestPoint = this.getPointAtAngle(normalizedAngle, params, totalCount)
    const distance = targetPoint.distanceTo(closestPoint)
    
    return {
      point: closestPoint,
      index: closestIndex,
      distance,
      angle: normalizedAngle
    }
  }
  
  /**
   * Get arc length between two indices
   */
  public getArcLength(startIndex: number, endIndex: number, totalCount: number, params: FieldParameters): number {
    const radius = this.calculateRadius(params, totalCount)
    const startAngle = this.calculateAngle(startIndex, totalCount)
    const endAngle = this.calculateAngle(endIndex, totalCount)
    
    let angleDiff = endAngle - startAngle
    if (angleDiff < 0) angleDiff += Math.PI * 2
    
    return radius * angleDiff
  }
  
  /**
   * Check if point is inside circle
   */
  public isPointInside(point: Vector3, params: FieldParameters, totalCount: number): boolean {
    const radius = this.calculateRadius(params, totalCount)
    const center = new Vector3()
    const distance = point.distanceTo(center)
    return distance < radius
  }
  
  /**
   * Get evenly distributed points on circle
   */
  public getEvenlyDistributedPoints(count: number, params: FieldParameters): Vector3[] {
    const points: Vector3[] = []
    const radius = this.calculateRadius(params, count)
    
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2
      const point = CurveUtils.getPointOnCircle(
        new Vector3(),
        radius,
        angle,
        params.customUpVector
      )
      points.push(point)
    }
    
    return points
  }
} 